<template>
    <div class="main-layout__container">
        <div class="content__header">
            <div class="content-header__title">Отчет по ставкам</div>

            <div class="content-header__background content-header__background--2-lines-filters"></div>
        </div>

        <FilterPanel
            :columns="4"
            @search="onSearch"
            @reset="onReset"
        >
            <UISelect
                id="lot_status"
                v-model="reportAuctionsFilter.lot_status"
                :options="statusOptions"
                label="Статус"
                multiple
            />

            <UISelect
                id="brand_id"
                v-model="reportAuctionsFilter.brand_id"
                :options="brandOptions"
                label="Марка"
            />

            <UISelect
                id="model_id"
                v-model="reportAuctionsFilter.model_id"
                :options="modelOptions"
                label="Модель"
                multiple
                searchable
            />

            <UIInput
                id="vin"
                v-model="reportAuctionsFilter.vin"
                label="VIN"
            />

            <UIInput
                id="lot_id"
                v-model="reportAuctionsLotIdFilter"
                label="Номер лота"
            />

            <UIDatePicker
                v-model="reportAuctionsFilter.auction_from_date"
                label="Дата от"
                :options="{ enableTime: false, dateFormat: 'd.m.Y' }"
            />

            <UIDatePicker
                v-model="reportAuctionsFilter.auction_to_date"
                label="Дата до"
                :options="{ enableTime: false, dateFormat: 'd.m.Y' }"
            />
        </FilterPanel>

        <UISearchResults
            :count="bidReportAuctionsPaginationMeta?.pagination.total || 0"
            :initial-sort="selectedSort"
            :sort-options="sortOptions"
            @update:sort="onSortChange"
        />

        <UILoader v-if="isBidReportAuctionsLoading" class="listable" />

        <div v-else class="content__body">
            <LotList :auctions="arBidReportAuctions" />
        </div>

        <div class="content__footer">
            <TablePagination
                :total-pages="bidReportAuctionsPaginationMeta?.pagination.last_page ?? 1"
                :current-page="bidReportAuctionsPaginationMeta?.pagination.current_page ?? 1"
                :items-per-page="bidReportAuctionsPaginationMeta?.pagination.per_page ?? 10"
                @page-change="changePage"
            />

            <div class="reports__button">
                <UISimpleButton
                    v-if="arBidReportAuctions.length > 0"
                    :loading="downloadLoading"
                    @clicked="downloadReport"
                >
                    Скачать отчёт по лотам
                </UISimpleButton>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
    import { computed, onMounted, ref } from 'vue';

    import { SelectOption, SortOption } from '@/types/form.type.ts';
    import { getAuctionStatusSelectOptions } from '@/types/auction.type.ts';
    import { Model } from '@/types/model.type.ts';
    import { ReportAuctionsRequestFilter } from '@/types/report.type.ts';

    import useDocumentDownloader from '@/utils/useDocumentDownloader.ts';
    import useModelRepository from '@/repositories/model/useModelRepository.ts';
    import useReportRepository from '@/repositories/report/useReportRepository.ts';
    import { useReportAuctionsFilterStore } from '@/stores/reports/report-auctions-filter.ts';

    import { getBrandSelectOptions } from '@/utils/SelectOptionFillers.ts';
    import { formatToUTC } from '@/utils/DateTimeFormatter.ts';

    import FilterPanel from '@/components/shared/FilterPanel/FilterPanel.vue';
    import TablePagination from '@/components/shared/TablePagination/TablePagination.vue';

    import UIDatePicker from '@/components/ui/UIDatePicker/UIDatePicker.vue';
    import UIInput from '@/components/ui/UIInput/UIInput.vue';
    import UILoader from '@/components/ui/UILoader/UILoader.vue';
    import UISearchResults from '@/components/ui/UISearchResults/UISearchResults.vue';
    import UISelect from '@/components/ui/UISelect/UISelect.vue';
    import UISimpleButton from '@/components/ui/UISimpleButton/UISimpleButton.vue';

    import LotList from '@/views/reports/BidReportList/components/LotList/LotList.vue';

    const {
        arBidReportAuctions,
        bidReportAuctionsPaginationMeta,
        isBidReportAuctionsLoading,
        getBidReportAuctions,
    } = useReportRepository();
    const { getAllActiveModels } = useModelRepository();
    const { downloadBidReports } = useDocumentDownloader();
    const reportAuctionsFilterStore = useReportAuctionsFilterStore();

    const reportAuctionsFilter = ref<ReportAuctionsRequestFilter>({ ...reportAuctionsFilterStore.$state });

    const statusOptions = ref<SelectOption[]>(getAuctionStatusSelectOptions());
    const brandOptions = ref<SelectOption[]>(getBrandSelectOptions());
    const arModels = ref<Model[]>([]);
    const modelOptions = computed<SelectOption[]>(() => {
        return arModels.value
            .filter((model: Model) => {
                if (reportAuctionsFilter.value.brand_id === undefined) {
                    return true;
                }

                return reportAuctionsFilter.value.brand_id === model.brand_id;
            })
            .map((model: Model) => ({
                label: model.name,
                value: model.id,
            }));
    });
    const selectedSort = ref<string>('start_date_desc');
    const sortOptions = ref<SortOption[]>([
        { label: 'по дате начала аукциона (1-9)', value: 'start_date_asc' },
        { label: 'по дате начала аукциона (9-1)', value: 'start_date_desc' },
        { label: 'по дате окончания аукциона (1-9)', value: 'start_end_asc' },
        { label: 'по дате окончания аукциона (9-1)', value: 'start_end_desc' },
    ]);

    const downloadLoading = ref<boolean>(false);

    const reportAuctionsLotIdFilter = computed<string>({
        get: () => reportAuctionsFilter.value.lot_id !== undefined ? `${reportAuctionsFilter.value.lot_id}` : '',
        set: (lotId) => {
            reportAuctionsFilter.value.lot_id = lotId.match(/^\d+$/) ? parseInt(lotId) : undefined;
        },
    });

    function onSearch(): void {
        reportAuctionsFilter.value.page = 1;
        fetchAuctions();
    }

    function onReset(): void {
        reportAuctionsFilter.value = {
            lot_status: undefined,
            brand_id: undefined,
            model_id: undefined,
            vin: '',
            lot_id: undefined,
            auction_from_date: '',
            auction_to_date: '',
            page: 1,
        };

        fetchAuctions();
    }

    function changePage(page: number): void {
        reportAuctionsFilter.value.page = page;
        fetchAuctions();
    }

    function onSortChange(sort: string| number): any {
        selectedSort.value = String(sort);

        switch (sort) {
            case 'start_date_asc':
                reportAuctionsFilter.value.sort = [{ start_date: 'asc' }];
                break;
            case 'start_date_desc':
                reportAuctionsFilter.value.sort = [{ start_date: 'desc' }];
                break;
            case 'start_end_asc':
                reportAuctionsFilter.value.sort = [{ end_date: 'asc' }];
                break;
            case 'start_end_desc':
                reportAuctionsFilter.value.sort = [{ end_date: 'desc' }];
                break;
        }

        fetchAuctions();
    }

    function downloadReport(): void {
        downloadLoading.value = true;
        downloadBidReports('report-by-bids-in-lots.xlsx', mapReportAuctionsFilterToRequestFilterWithoutPageParams())
            .finally(() => {
                downloadLoading.value = false;
            });
    }

    function mapReportAuctionsFilterToRequestFilter(): ReportAuctionsRequestFilter {
        const requestFilter: ReportAuctionsRequestFilter = mapReportAuctionsFilterToRequestFilterWithoutPageParams();

        requestFilter.page = reportAuctionsFilter.value.page;
        requestFilter.sort = reportAuctionsFilter.value.sort;

        return requestFilter;
    }

    function mapReportAuctionsFilterToRequestFilterWithoutPageParams(): ReportAuctionsRequestFilter {
        const requestFilter: ReportAuctionsRequestFilter = {};

        if (reportAuctionsFilter.value.lot_status !== undefined && reportAuctionsFilter.value.lot_status.length > 0) {
            requestFilter.lot_status = reportAuctionsFilter.value.lot_status;
        }

        if (reportAuctionsFilter.value.brand_id !== undefined) {
            requestFilter.brand_id = reportAuctionsFilter.value.brand_id;
        }

        if (reportAuctionsFilter.value.model_id !== undefined && reportAuctionsFilter.value.model_id.length > 0) {
            requestFilter.model_id = reportAuctionsFilter.value.model_id;
        }

        if (reportAuctionsFilter.value.vin !== undefined && reportAuctionsFilter.value.vin.length > 0) {
            requestFilter.vin = reportAuctionsFilter.value.vin;
        }

        if (reportAuctionsFilter.value.lot_id !== undefined) {
            requestFilter.lot_id = reportAuctionsFilter.value.lot_id;
        }

        if (reportAuctionsFilter.value.auction_from_date !== undefined && reportAuctionsFilter.value.auction_from_date.length > 0) {
            requestFilter.auction_from_date = formatToUTC(reportAuctionsFilter.value.auction_from_date, {
                fromFormat: 'dd.LL.yyyy',
                toFormat: 'yyyy-LL-dd',
                withTime: false,
            });
        }

        if (reportAuctionsFilter.value.auction_to_date !== undefined && reportAuctionsFilter.value.auction_to_date.length > 0) {
            requestFilter.auction_to_date = formatToUTC(reportAuctionsFilter.value.auction_to_date, {
                fromFormat: 'dd.LL.yyyy',
                toFormat: 'yyyy-LL-dd',
                withTime: false,
            });
        }

        return requestFilter;
    }

    function fetchAuctions(): void {
        reportAuctionsFilterStore.$patch({ ...reportAuctionsFilter.value });
        getBidReportAuctions(mapReportAuctionsFilterToRequestFilter());
    }

    function fetchFilterOptions(): void {
        getAllActiveModels().then((models: Model[]) => {
            arModels.value = models;
        });
    }

    onMounted(() => {
        fetchAuctions();
        fetchFilterOptions();
    });
</script>

<style scoped lang="scss">
    @use './BidReportList.scss' as *;
</style>
